import { Users, Calendar, Settings, Sliders, UserPlus, ChevronLeft, ChevronRight, Timer, RectangleHorizontal, Bell, Lightbulb, Building2, Euro, AlertCircle, Home, History, Mail, FlaskConical } from "lucide-react";
import { cn } from "@/lib/utils";
import { PlayPauseIcon } from "./PlayPauseIcon";
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "@/lib/router-compat";
import { useTranslation } from "@/i18n/LanguageContext";

interface BottomNavigationProps {
  role: 'husselaar' | 'club-beheerder' | 'player' | 'super-admin';
  activeView: string;
  onViewChange: (view: string) => void;
  currentTimeSlot?: string | null;
  hasActiveSession?: boolean;
}

export const BottomNavigation = ({ role, activeView, onViewChange, currentTimeSlot, hasActiveSession = true }: BottomNavigationProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Base admin items that husselaar can see
  const husselaarItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'session', label: 'Sessies', icon: Calendar },
    { id: 'live', label: 'Live', icon: Timer },
    { id: 'players', label: 'Aanmeldingen', icon: Users },
    { id: 'members', label: 'Leden', icon: Users },
    { id: 'history', label: 'Indeling', icon: History },
    { id: 'playerHistory', label: 'Mijn historie', icon: History },
    { id: 'notifications', label: 'Meldingen', icon: Bell },
  ];

  // Club-beheerder menu items
  const clubBeheerderItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'club', label: 'Club', icon: Building2 },
    { id: 'session', label: 'Sessies', icon: Calendar },
    { id: 'live', label: 'Live', icon: Timer },
    { id: 'players', label: 'Aanmeldingen', icon: Users },
    { id: 'members', label: 'Leden', icon: Users },
    { id: 'history', label: 'Indeling', icon: History },
    { id: 'playerHistory', label: 'Mijn historie', icon: History },
    { id: 'notifications', label: 'Meldingen', icon: Bell },
    { id: 'settings', label: 'Instellingen', icon: Sliders },
    { id: 'howTo', label: 'Handleiding', icon: Lightbulb, isLink: true, href: '/admin-handleiding' },
  ];

  // Super-admin items - platform-level management only (no users view)
  const superAdminItems = [
    { id: 'home', label: 'Home', icon: Home },
    { id: 'clubs', label: 'Clubs', icon: Building2 },
    { id: 'marketing', label: 'Marketing', icon: Mail },
    { id: 'financials', label: 'Financieel', icon: Euro },
    { id: 'notifications', label: 'Meldingen', icon: Bell },
    { id: 'settings', label: 'Platform', icon: Sliders },
    { id: 'testdebug', label: 'Test', icon: FlaskConical },
  ];

  const playerItemsFull = [
    { id: 'management', label: t('nav.registerUnregister'), icon: PlayPauseIcon as any },
    { id: 'current', label: t('nav.live'), icon: Timer },
    { id: 'players', label: t('nav.players'), icon: Users },
    { id: 'history', label: t('nav.history'), icon: History },
    { id: 'howItWorks', label: t('nav.howItWorks'), icon: Lightbulb, isLink: true, href: '/how-it-works' },
  ];

  const playerItemsLimited = [
    { id: 'management', label: t('nav.registerUnregister'), icon: PlayPauseIcon as any },
    { id: 'howItWorks', label: t('nav.howItWorks'), icon: Lightbulb, isLink: true, href: '/how-it-works' },
  ];

  const playerItems = (role === 'player' && !hasActiveSession) ? playerItemsLimited : playerItemsFull;

  const getItemsForRole = () => {
    switch (role) {
      case 'super-admin':
        return superAdminItems;
      case 'club-beheerder':
        return clubBeheerderItems;
      case 'husselaar':
        return husselaarItems;
      default:
        return playerItems;
    }
  };

  const items = getItemsForRole();

  const updateScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    updateScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', updateScrollButtons);
      window.addEventListener('resize', updateScrollButtons);
      return () => {
        container.removeEventListener('scroll', updateScrollButtons);
        window.removeEventListener('resize', updateScrollButtons);
      };
    }
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50">
      <div className="bg-background/80 backdrop-blur-sm border-t border-border shadow-lg">
        <div className="max-w-7xl mx-auto relative">
          {/* Left scroll button */}
          {canScrollLeft && (
            <button
              onClick={() => scroll('left')}
              className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-background to-transparent z-10 flex items-center justify-center hover:bg-muted transition-all"
            >
              <ChevronLeft className="h-6 w-6 text-foreground" />
            </button>
          )}
          
          {/* Right scroll button */}
          {canScrollRight && (
            <button
              onClick={() => scroll('right')}
              className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background to-transparent z-10 flex items-center justify-center hover:bg-muted transition-all"
            >
              <ChevronRight className="h-6 w-6 text-foreground" />
            </button>
          )}
          
          <div 
            ref={scrollContainerRef}
            className="overflow-x-auto scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            onWheel={(e) => {
              e.preventDefault();
              if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollLeft += e.deltaY;
                updateScrollButtons();
              }
            }}
          >
            <div className="flex gap-1 p-3 min-w-max">
              {items.map((item) => {
                const isLink = 'isLink' in item && item.isLink;
                const href = 'href' in item ? item.href : undefined;
                
                const handleClick = () => {
                  if (isLink && href) {
                    navigate(href);
                  } else {
                    onViewChange(item.id);
                  }
                };
                
                return (
                  <button
                    key={item.id}
                    onClick={handleClick}
                    className={cn(
                      "flex flex-col items-center justify-center gap-1 p-3 rounded-lg transition-all min-w-[calc(25vw-1rem)] max-w-[120px]",
                      activeView === item.id 
                        ? "bg-primary/90 text-primary-foreground" 
                        : "text-foreground hover:bg-muted/80"
                    )}
                  >
                    {item.id === 'management' ? (
                      <PlayPauseIcon className="h-5 w-5" />
                    ) : (
                      <item.icon className="h-5 w-5" strokeWidth={2.5} />
                    )}
                    <span className="text-xs whitespace-nowrap" style={{ fontFamily: '"Righteous", "Be Vietnam Pro", sans-serif', fontWeight: 400 }}>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};
